import React, { useState, useEffect, useMemo } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

// Import utility functions - updated to work with existing modules
import { calculateAllMetrics, calculatePortfolioRisk } from './metrics.js';
import { projectDividends, calcYieldOnCost } from './dividends.js';
import { longPutPayoff, coveredCallPayoff } from './hedges.js';
import { runSingleScenario, calculateVaR } from './stress.js';

// Mock data - in real app this would come from API
const mockAccountSnapshot = {
  "asof": "2025-08-17T15:30:00Z",
  "cash": 5000,
  "nlv": 100000,
  "margin_used": 20000,
  "margin_available": 30000,
  "buying_power": 40000,
  "equity": 80000,
  "positions": [
    {
      "ticker": "CLM",
      "shares": 200,
      "price": 8.0,
      "avg_cost": 7.5,
      "maintenance_margin_rate": 0.25,
      "dividend_yield": 15.0,
      "dividends_last_3m": 120.0
    },
    {
      "ticker": "AAPL", 
      "shares": 100,
      "price": 150,
      "avg_cost": 145,
      "maintenance_margin_rate": 0.25,
      "dividend_yield": 0.5
    },
    {
      "ticker": "MSFT",
      "shares": 50, 
      "price": 300,
      "avg_cost": 280,
      "maintenance_margin_rate": 0.25,
      "dividend_yield": 2.8
    }
  ],
  "options_positions": [
    {
      "underlying": "QQQ",
      "right": "put",
      "strike": 380.0,
      "expiry": "2025-09-20",
      "contracts": 1,
      "multiplier": 100,
      "premium_paid": 400.0,
      "premium_received": 0.0
    }
  ],
  "history": {
    "monthly_dividends": [
      {"month": "2025-06", "amount": 300.0},
      {"month": "2025-07", "amount": 320.0},
      {"month": "2025-08", "amount": 310.0}
    ]
  }
};

// Helper functions to bridge the gap between enhanced UI and existing modules
const calcNLV = (cash: number, holdings: number, margin: number) => cash + holdings;
const calcLeverage = (cash: number, holdings: number, nlv: number) => nlv > 0 ? (nlv - cash) / nlv : 0;
const calcMarginHealth = (bufferPct: number) => Math.max(1, Math.min(10, 10 - (bufferPct * 10)));
const calcExcessEquity = (nlv: number, margin: number, maintenanceRate: number) => nlv - margin;
const identifyMarginCallRisk = (data: any) => data.nlv < (data.marginUsed * 1.25);
const calcPortfolioValue = (positions: any[]) => positions.reduce((sum, pos) => sum + (pos.shares * pos.price), 0);
const calcProjectedMonthlyDividends = (positions: any[]) => positions.reduce((sum, pos) => sum + (pos.shares * pos.price * (pos.dividend_yield || 0) / 100 / 12), 0);
const calcDividendYield = (positions: any[]) => {
  const totalValue = calcPortfolioValue(positions);
  const annualDividends = positions.reduce((sum, pos) => sum + (pos.shares * pos.price * (pos.dividend_yield || 0) / 100), 0);
  return totalValue > 0 ? (annualDividends / totalValue) * 100 : 0;
};
const calcMonthlyDividendHistory = (history: any) => history?.monthly_dividends || [];
const calculateProtectionPercentage = (nlv: number, hedgeConfig: any, volatility: number) => {
  const hedgeValue = hedgeConfig.contracts * 100 * hedgeConfig.strike;
  return nlv > 0 ? (hedgeValue / nlv) * 100 : 0;
};
const analyzeOptionsPositions = (positions: any[]) => ({
  totalPositions: positions.length,
  totalValue: positions.reduce((sum, pos) => sum + (pos.premium_paid || 0), 0)
});

const IBKRTracker: React.FC = () => {
  // State management
  const [accountData, setAccountData] = useState(mockAccountSnapshot);
  const [cashInput, setCashInput] = useState(mockAccountSnapshot.cash.toString());
  const [holdingsInput, setHoldingsInput] = useState('');
  const [marginInput, setMarginInput] = useState(mockAccountSnapshot.margin_used.toString());
  const [selectedStressScenario, setSelectedStressScenario] = useState('15');
  const [selectedHedge, setSelectedHedge] = useState('none');
  const [hedgeParams, setHedgeParams] = useState({
    strike: '380',
    contracts: '1',
    premium: '400'
  });
  const [stressResults, setStressResults] = useState<any>(null);
  const [uploadStatus, setUploadStatus] = useState('');
  const [activeTab, setActiveTab] = useState('overview');

  // Initialize holdings input from positions
  useEffect(() => {
    const totalHoldings = calcPortfolioValue(accountData.positions);
    setHoldingsInput(totalHoldings.toString());
  }, [accountData.positions]);

  // Calculated metrics
  const metrics = useMemo(() => {
    const cash = parseFloat(cashInput) || 0;
    const holdings = parseFloat(holdingsInput) || 0;
    const margin = parseFloat(marginInput) || 0;
    
    const nlv = calcNLV(cash, holdings, margin);
    const leverage = calcLeverage(cash, holdings, nlv);
    const excessEquity = calcExcessEquity(nlv, margin, 0.25);
    const bufferPct = excessEquity / (nlv || 1);
    const marginHealth = calcMarginHealth(bufferPct);
    const marginCallRisk = identifyMarginCallRisk({
      nlv,
      marginUsed: margin,
      maintenanceMargin: 0.25
    });

    return {
      nlv,
      leverage,
      marginHealth,
      excessEquity,
      bufferPct,
      marginCallRisk
    };
  }, [cashInput, holdingsInput, marginInput]);

  // Dividend metrics
  const dividendMetrics = useMemo(() => {
    const monthlyDividends = calcProjectedMonthlyDividends(accountData.positions);
    const annualDividends = monthlyDividends * 12;
    const portfolioValue = calcPortfolioValue(accountData.positions);
    const portfolioYield = calcDividendYield(accountData.positions);
    const monthlyHistory = calcMonthlyDividendHistory(accountData.history);
    const yoc = calcYieldOnCost(annualDividends, portfolioValue);

    return {
      monthlyDividends,
      annualDividends,
      portfolioYield,
      monthlyHistory,
      yoc
    };
  }, [accountData]);

  // Options analysis
  const optionsAnalysis = useMemo(() => {
    return analyzeOptionsPositions(accountData.options_positions);
  }, [accountData.options_positions]);

  // Handle CSV upload
  const handleCSVUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploadStatus('Processing...');

    try {
      const text = await file.text();
      const lines = text.split('\n').filter(line => line.trim());
      
      if (lines.length < 2) {
        setUploadStatus('Empty file');
        return;
      }

      const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
      const positions: any[] = [];

      for (let i = 1; i < lines.length; i++) {
        const values = lines[i].split(',');
        if (values.length < 3) continue;

        const position: any = {};
        headers.forEach((header, index) => {
          if (values[index]) {
            const value = values[index].trim();
            switch (header) {
              case 'symbol':
              case 'ticker':
                position.ticker = value;
                break;
              case 'shares':
                position.shares = parseFloat(value) || 0;
                break;
              case 'price':
                position.price = parseFloat(value) || 0;
                break;
              case 'dividend yield':
              case 'dividendyield':
                position.dividend_yield = parseFloat(value) || 0;
                break;
              default:
                position[header] = value;
            }
          }
        });

        if (position.ticker && position.shares && position.price) {
          positions.push({
            ...position,
            avg_cost: position.price,
            maintenance_margin_rate: 0.25
          });
        }
      }

      if (positions.length === 0) {
        setUploadStatus('Invalid CSV format');
        return;
      }

      setAccountData(prev => ({ ...prev, positions }));
      setUploadStatus(`${positions.length} positions loaded`);
      
      // Update holdings input
      const totalValue = calcPortfolioValue(positions);
      setHoldingsInput(totalValue.toString());

    } catch (error) {
      setUploadStatus('Invalid data format');
    }
  };

  // Handle stress test
  const runStressTest = () => {
    const portfolio = {
      cash: parseFloat(cashInput) || 0,
      marginUsed: parseFloat(marginInput) || 0,
      holdings: accountData.positions.map(pos => ({
        symbol: pos.ticker,
        quantity: pos.shares,
        price: pos.price
      }))
    };

    const scenario = parseFloat(selectedStressScenario);
    const result = runSingleScenario(portfolio, scenario);
    
    setStressResults({
      originalNLV: metrics.nlv,
      newNLV: result.stressedNLV,
      hedgePayoff: 0,
      finalNLV: result.stressedNLV,
      marginCallRisk: result.marginCall,
      protection: 0
    });
  };

  // Chart data
  const stressChartData = useMemo(() => {
    if (!stressResults) return [];
    
    const scenarios = [5, 10, 15, 20, 25, 30];
    return scenarios.map(scenario => {
      const portfolio = {
        cash: parseFloat(cashInput) || 0,
        marginUsed: parseFloat(marginInput) || 0,
        holdings: accountData.positions.map(pos => ({
          symbol: pos.ticker,
          quantity: pos.shares,
          price: pos.price
        }))
      };
      
      const result = runSingleScenario(portfolio, scenario);
      return {
        drop: scenario,
        nlv: result.stressedNLV,
        marginCall: result.marginCall ? result.stressedNLV : null
      };
    });
  }, [metrics.nlv, marginInput, stressResults, cashInput, accountData.positions]);

  const portfolioAllocation = useMemo(() => {
    return accountData.positions.map(pos => ({
      name: pos.ticker,
      value: pos.shares * pos.price,
      percentage: ((pos.shares * pos.price) / calcPortfolioValue(accountData.positions)) * 100
    }));
  }, [accountData.positions]);

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-6" data-testid="ibkr-tracker">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-lg p-6">
        <h1 className="text-3xl font-bold text-gray-800 mb-2">IBKR Margin Tracker</h1>
        <p className="text-gray-600">Real-time portfolio analysis, margin monitoring, and risk management</p>
        <p className="text-sm text-gray-500 mt-1">Last updated: {accountData.asof}</p>
      </div>

      {/* Navigation Tabs */}
      <div className="bg-white rounded-lg shadow-lg">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8" aria-label="Tabs">
            {[
              { id: 'overview', name: 'Account Overview' },
              { id: 'dividends', name: 'Dividend Analytics' },
              { id: 'hedges', name: 'Options Hedges' },
              { id: 'stress', name: 'Stress Testing' }
            ].map((tab) => (
              <button
                key={tab.id}
                data-testid={`${tab.id}-tab`}
                onClick={() => setActiveTab(tab.id)}
                className={`${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
              >
                {tab.name}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Account Overview Tab */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          {/* Input Section */}
          <div className="bg-white rounded-lg shadow-lg p-6">
            <h2 className="text-xl font-semibold mb-4">Portfolio Inputs</h2>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label htmlFor="cash-input" className="block text-sm font-medium text-gray-700 mb-2">
                  Cash Position
                </label>
                <input
                  id="cash-input"
                  data-testid="cash-input"
                  type="text"
                  value={cashInput}
                  onChange={(e) => setCashInput(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="5000"
                />
              </div>
              <div>
                <label htmlFor="holdings-input" className="block text-sm font-medium text-gray-700 mb-2">
                  Holdings Value
                </label>
                <input
                  id="holdings-input"
                  data-testid="holdings-input"
                  type="text"
                  value={holdingsInput}
                  onChange={(e) => setHoldingsInput(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="95000"
                />
              </div>
              <div>
                <label htmlFor="margin-input" className="block text-sm font-medium text-gray-700 mb-2">
                  Margin Used
                </label>
                <input
                  id="margin-input"
                  data-testid="margin-input"
                  type="text"
                  value={marginInput}
                  onChange={(e) => setMarginInput(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="20000"
                />
              </div>
              <div>
                <label htmlFor="csv-upload" className="block text-sm font-medium text-gray-700 mb-2">
                  Upload CSV
                </label>
                <input
                  id="csv-upload"
                  data-testid="csv-upload"
                  type="file"
                  accept=".csv"
                  onChange={handleCSVUpload}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                {uploadStatus && (
                  <p className="text-xs text-gray-600 mt-1">{uploadStatus}</p>
                )}
              </div>
            </div>
          </div>

          {/* Key Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="bg-white rounded-lg shadow-lg p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-2">Net Liquidation Value</h3>
              <p data-testid="nlv-display" className="text-3xl font-bold text-blue-600">
                ${metrics.nlv.toLocaleString()}
              </p>
              <p className="text-sm text-gray-600 mt-1">Total Portfolio Value</p>
            </div>
            
            <div className="bg-white rounded-lg shadow-lg p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-2">Leverage Ratio</h3>
              <p data-testid="leverage-display" className="text-3xl font-bold text-green-600">
                {metrics.leverage.toFixed(2)}x
              </p>
              <p className="text-sm text-gray-600 mt-1">Current Leverage</p>
            </div>
            
            <div className="bg-white rounded-lg shadow-lg p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-2">Margin Health</h3>
              <div className="flex items-center">
                <p data-testid="margin-health-score" className={`text-3xl font-bold ${
                  metrics.marginHealth <= 3 ? 'text-green-600' : 
                  metrics.marginHealth <= 7 ? 'text-yellow-600' : 'text-red-600'
                }`}>
                  {metrics.marginHealth}/10
                </p>
                <div className="ml-3">
                  <div className="w-16 h-2 bg-gray-200 rounded-full">
                    <div 
                      className={`h-2 rounded-full ${
                        metrics.marginHealth <= 3 ? 'bg-green-500' : 
                        metrics.marginHealth <= 7 ? 'bg-yellow-500' : 'bg-red-500'
                      }`}
                      style={{ width: `${Math.max(10, (11 - metrics.marginHealth) * 10)}%` }}
                    ></div>
                  </div>
                </div>
              </div>
              <p className="text-sm text-gray-600 mt-1">Risk Assessment</p>
            </div>
            
            <div className="bg-white rounded-lg shadow-lg p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-2">Portfolio Value</h3>
              <p data-testid="portfolio-value" className="text-3xl font-bold text-purple-600">
                ${calcPortfolioValue(accountData.positions).toLocaleString()}
              </p>
              <p className="text-sm text-gray-600 mt-1">Holdings Only</p>
            </div>
          </div>

          {/* Warnings */}
          {parseFloat(cashInput) < 0 && (
            <div data-testid="negative-cash-warning" className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
              <div className="flex">
                <div className="ml-3">
                  <p className="text-sm text-yellow-700">
                    ⚠️ Negative cash position detected. This indicates borrowing against your portfolio.
                  </p>
                </div>
              </div>
            </div>
          )}

          {metrics.marginCallRisk && (
            <div data-testid="high-risk-warning" className="bg-red-50 border-l-4 border-red-400 p-4">
              <div className="flex">
                <div className="ml-3">
                  <p className="text-sm text-red-700">
                    🚨 High margin risk detected! Your account may be subject to a margin call.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Portfolio Allocation Chart */}
          <div className="bg-white rounded-lg shadow-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Portfolio Allocation</h3>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={portfolioAllocation}
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                    label={({ name, percentage }) => `${name} (${percentage.toFixed(1)}%)`}
                  >
                    {portfolioAllocation.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => [`${value.toLocaleString()}`, 'Value']} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      )}

      {/* Dividend Analytics Tab */}
      {activeTab === 'dividends' && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white rounded-lg shadow-lg p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-2">Monthly Dividends</h3>
              <p data-testid="projected-monthly" className="text-3xl font-bold text-green-600">
                ${dividendMetrics.monthlyDividends.toFixed(2)}
              </p>
              <p className="text-sm text-gray-600 mt-1">Projected</p>
            </div>
            
            <div className="bg-white rounded-lg shadow-lg p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-2">Annual Dividends</h3>
              <p data-testid="projected-annual" className="text-3xl font-bold text-green-600">
                ${dividendMetrics.annualDividends.toFixed(2)}
              </p>
              <p className="text-sm text-gray-600 mt-1">Projected</p>
            </div>
            
            <div className="bg-white rounded-lg shadow-lg p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-2">Portfolio Yield</h3>
              <p data-testid="portfolio-yield" className="text-3xl font-bold text-blue-600">
                {dividendMetrics.portfolioYield.toFixed(2)}%
              </p>
              <p className="text-sm text-gray-600 mt-1">Current Yield</p>
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-2">Dividend Yield</h3>
            <p data-testid="dividend-yield" className="text-2xl font-bold text-green-600">
              {dividendMetrics.yoc.toFixed(2)}%
            </p>
            <p className="text-sm text-gray-600 mt-1">Yield on Cost</p>
          </div>
        </div>
      )}

      {/* Options Hedges Tab */}
      {activeTab === 'hedges' && (
        <div className="space-y-6">
          <div className="bg-white rounded-lg shadow-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Hedge Configuration</h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Strategy</label>
                <select
                  data-testid="hedge-strategy-select"
                  value={selectedHedge}
                  onChange={(e) => setSelectedHedge(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="none">No Hedge</option>
                  <option value="longPut">Long Put Protection</option>
                  <option value="cashSecuredPut">Cash-Secured Put</option>
                  <option value="bearPutSpread">Bear Put Spread</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Strike Price</label>
                <input
                  data-testid="strike-input"
                  type="text"
                  value={hedgeParams.strike}
                  onChange={(e) => setHedgeParams(prev => ({ ...prev, strike: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Contracts</label>
                <input
                  data-testid="contracts-input"
                  type="text"
                  value={hedgeParams.contracts}
                  onChange={(e) => setHedgeParams(prev => ({ ...prev, contracts: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Premium</label>
                <input
                  data-testid="premium-input"
                  type="text"
                  value={hedgeParams.premium}
                  onChange={(e) => setHedgeParams(prev => ({ ...prev, premium: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
            
            {selectedHedge !== 'none' && (
              <div data-testid="hedge-calculator" className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-blue-50 p-4 rounded-lg">
                  <h4 className="font-medium text-gray-900">Hedge Cost</h4>
                  <p data-testid="hedge-cost" className="text-2xl font-bold text-blue-600">
                    ${(parseFloat(hedgeParams.premium) * parseFloat(hedgeParams.contracts)).toFixed(2)}
                  </p>
                </div>
                <div className="bg-green-50 p-4 rounded-lg">
                  <h4 className="font-medium text-gray-900">Protection Level</h4>
                  <p data-testid="protection-percentage" className="text-2xl font-bold text-green-600">
                    {calculateProtectionPercentage(metrics.nlv, {
                      strike: parseFloat(hedgeParams.strike),
                      contracts: parseFloat(hedgeParams.contracts),
                      premium: parseFloat(hedgeParams.premium)
                    }, 0.15).toFixed(1)}%
                  </p>
                </div>
                <div className="bg-purple-50 p-4 rounded-lg">
                  <h4 className="font-medium text-gray-900">Current Options</h4>
                  <p className="text-2xl font-bold text-purple-600">
                    {optionsAnalysis.totalPositions}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Stress Testing Tab */}
      {activeTab === 'stress' && (
        <div className="space-y-6">
          <div className="bg-white rounded-lg shadow-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Stress Test Configuration</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Market Drop Scenario</label>
                <select
                  data-testid="stress-scenario-select"
                  value={selectedStressScenario}
                  onChange={(e) => setSelectedStressScenario(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="5">5% Drop (Minor Correction)</option>
                  <option value="10">10% Drop (Correction)</option>
                  <option value="15">15% Market Drop</option>
                  <option value="20">20% Drop (Bear Market)</option>
                  <option value="30">30% Drop (Major Crash)</option>
                  <option value="40">40% Drop (Severe Crash)</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Hedge Strategy</label>
                <select
                  data-testid="stress-hedge-select"
                  value={selectedHedge}
                  onChange={(e) => setSelectedHedge(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="none">No Hedge</option>
                  <option value="longPut">Long Put</option>
                  <option value="bearPutSpread">Bear Put Spread</option>
                </select>
              </div>
              <div>
                <button
                  data-testid="run-stress-test-button"
                  onClick={runStressTest}
                  className="w-full mt-6 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  Run Stress Test
                </button>
              </div>
            </div>
          </div>

          {stressResults && (
            <div data-testid="stress-test-result" className="bg-white rounded-lg shadow-lg p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Stress Test Results</h3>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-blue-50 p-4 rounded-lg">
                  <h4 className="font-medium text-gray-900">Original NLV</h4>
                  <p className="text-2xl font-bold text-blue-600">
                    ${stressResults.originalNLV.toLocaleString()}
                  </p>
                </div>
                <div className="bg-red-50 p-4 rounded-lg">
                  <h4 className="font-medium text-gray-900">After Drop</h4>
                  <p data-testid="stress-result-nlv" className="text-2xl font-bold text-red-600">
                    ${stressResults.newNLV.toLocaleString()}
                  </p>
                </div>
                <div className="bg-green-50 p-4 rounded-lg">
                  <h4 className="font-medium text-gray-900">Hedge Payoff</h4>
                  <p className="text-2xl font-bold text-green-600">
                    ${stressResults.hedgePayoff.toLocaleString()}
                  </p>
                </div>
                <div className="bg-purple-50 p-4 rounded-lg">
                  <h4 className="font-medium text-gray-900">Final NLV</h4>
                  <p className="text-2xl font-bold text-purple-600">
                    ${stressResults.finalNLV.toLocaleString()}
                  </p>
                </div>
              </div>
              
              <div className="mt-4 flex items-center">
                <span className="text-sm text-gray-600">Margin Call Risk:</span>
                <span data-testid="margin-call-risk" className={`ml-2 px-2 py-1 rounded text-sm font-medium ${
                  stressResults.marginCallRisk 
                    ? 'bg-red-100 text-red-800' 
                    : 'bg-green-100 text-green-800'
                }`}>
                  {stressResults.marginCallRisk ? 'YES' : 'NO'}
                </span>
              </div>

              <div className="mt-4">
                <span className="text-sm text-gray-600">Protection Level: </span>
                <span className="text-lg font-medium text-green-600">
                  {stressResults.protection.toFixed(1)}%
                </span>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default IBKRTracker;